import React, { useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import remarkGfm from 'remark-gfm'
import IconifyIcon from '../wrapper/IconifyIcon'
import QuantumOddsCTA from './QuantumOddsCTA'
import { OracleChatWelcome, OracleChatBanner } from './OracleChatWelcome'
import './PolymarketHub.css'

const message = (role, text = '') => ({ id: `${role}-${Date.now()}-${Math.random()}`, role, text })

const parseSseStream = async (stream, onEvent) => {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const parseBlock = (block) => {
    let type = 'message'
    const data = []
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) type = line.slice(6).trim()
      if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
    })
    if (data.length) onEvent(type, JSON.parse(data.join('\n')))
  }
  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n')
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      parseBlock(buffer.slice(0, boundary))
      buffer = buffer.slice(boundary + 2)
      boundary = buffer.indexOf('\n\n')
    }
    if (done) break
  }
  if (buffer.trim()) parseBlock(buffer)
}

const PolymarketHub = ({ authenticatedFetch, user, onRequireAuth, active = true }) => {
  const { t, i18n } = useTranslation()
  const tr = (key) => t(`oracleChat.${key}`)
  const localized = /^(en|es)/.test(i18n.resolvedLanguage || i18n.language || '')
  const [chatInput, setChatInput] = useState('')
  const [chatMessages, setChatMessages] = useState([])
  const [chatConversationId, setChatConversationId] = useState(null)
  const [chatLoading, setChatLoading] = useState(false)
  const [chatActivity, setChatActivity] = useState('')
  const [chatError, setChatError] = useState('')
  const inputRef = useRef(null)

  const askPolymarket = async (rawPrompt) => {
    const prompt = String(rawPrompt || '').trim()
    if (!prompt || chatLoading) return
    if (!user) {
      setChatInput(prompt)
      onRequireAuth()
      return
    }
    const assistant = { ...message('assistant'), streaming: true }
    setChatMessages((current) => [...current, message('user', prompt), assistant])
    setChatInput('')
    setChatError('')
    setChatLoading(true)
    setChatActivity(tr('polymarketThinking'))
    const updateAssistant = (text) => setChatMessages((current) => current.map((item) => item.id === assistant.id ? { ...item, text: item.text + text } : item))
    try {
      const response = await authenticatedFetch('/api/oracle/chat', {
        method: 'POST',
        body: JSON.stringify({ message: prompt, conversationId: chatConversationId, mode: 'polymarket' }),
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error((localized ? tr('polymarketFailed') : payload.message || tr('polymarketFailed')))
      }
      await parseSseStream(response.body, (type, payload) => {
        if (type === 'ready') setChatConversationId(payload.conversationId)
        if (type === 'status') setChatActivity((localized ? tr('analysing') : payload.message || tr('analysing')))
        if (type === 'tool') setChatActivity(payload.state === 'completed' ? tr('organizing') : tr('consultingPolymarket'))
        if (type === 'delta') updateAssistant(payload.text || '')
        if (type === 'error') throw new Error((localized ? tr('interrupted') : payload.message || tr('interrupted')))
      })
    } catch (requestError) {
      setChatError(requestError.message)
      setChatMessages((current) => current.map((item) => item.id === assistant.id
        ? { ...item, failed: true, text: item.text || tr('incomplete') } : item))
    } finally {
      setChatMessages((current) => current.map((item) => item.id === assistant.id ? { ...item, streaming: false } : item))
      setChatLoading(false)
      setChatActivity('')
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      askPolymarket(chatInput)
    }
  }

  const copyAnswer = async (text) => navigator.clipboard?.writeText(text)

  return (
    <section className={`oracle-pm-hub ${chatMessages.length ? 'has-conversation' : 'is-empty'}`}>
      <section className="oracle-pm-general">
        {!chatMessages.length && <OracleChatWelcome oracle="polymarket" active={active} onSelect={(prompt) => { setChatInput(prompt); inputRef.current?.focus() }} />}
        <div className="oracle-pm-chat oracle-message-list" aria-live="polite">
          {chatMessages.map((item) => (
            <article key={item.id} className={`oracle-message is-${item.role}`}>
              <div className="oracle-message-avatar">{item.role === 'assistant' ? 'FM' : (user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}</div>
              <div className="oracle-message-content">
                <div className="oracle-message-label">{item.role === 'assistant' ? t('oracleHome.cards.polymarket.title') : tr('you')}</div>
                {item.text ? <div className="oracle-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.text}</ReactMarkdown>{item.role === 'assistant' && !item.streaming && !item.failed && <QuantumOddsCTA />}</div> : item.role === 'assistant' && item.streaming ? <div className="oracle-thinking"><i /><i /><i /></div> : null}
                {item.role === 'assistant' && item.text && !item.streaming && <button className="oracle-copy" type="button" onClick={() => copyAnswer(item.text)}><IconifyIcon icon="solar:copy-linear" width="16" /> {tr('copy')}</button>}
              </div>
            </article>
          ))}
          {chatLoading && chatActivity && <div className="oracle-activity"><span><i /></span><strong>{chatActivity}</strong></div>}
        </div>
        {chatError && <div className="oracle-error"><IconifyIcon icon="solar:danger-triangle-linear" width="18" />{chatError}</div>}
        <form className="oracle-composer oracle-pm-composer" onSubmit={(event) => { event.preventDefault(); askPolymarket(chatInput) }}>
          <textarea ref={inputRef} value={chatInput} onChange={(event) => setChatInput(event.target.value.slice(0, 2000))} onKeyDown={handleKeyDown} aria-label={tr('polymarketAsk')} placeholder={tr('polymarketPlaceholder')} rows={chatMessages.length ? 1 : 2} disabled={chatLoading} />
          <div className="oracle-composer-footer">
            {chatMessages.length > 0 && <div><IconifyIcon icon="solar:database-linear" width="15" /> {tr('polymarketData')}</div>}
            <button type="submit" className={`oracle-send-button ${!chatMessages.length ? 'oracle-send-text' : ''}`} disabled={!chatInput.trim() || chatLoading} aria-label={t('oracleCurrent.sendQuestion', { defaultValue: 'Enviar pergunta' })}>{chatMessages.length ? <IconifyIcon icon="solar:arrow-up-linear" width="20" /> : tr('send')}</button>
          </div>
        </form>
        {!chatMessages.length && <div className="oracle-pm-suggestions" aria-label={tr('polymarketSuggestionsLabel') }>
          {t('oracleChat.polymarketSuggestions', { returnObjects: true }).map((prompt) => <button type="button" key={prompt} onClick={() => askPolymarket(prompt)}>{prompt}</button>)}
        </div>}
        {!chatMessages.length && <OracleChatBanner />}
      </section>
    </section>
  )
}

export default PolymarketHub
